import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { useAuth } from './AuthContext';
import { fetchNotifications, markNotificationAsRead, deleteNotification, fetchNotificationCountByStatus } from '../services/notificationService';
import { storageKeys } from '../config/environment';
import {
    parseTransactionListenerMessage,
    getTransactionListenerId,
    extractListenerRef,
    isPaymentApprovedStatus
} from '../utils/transactionWebSocket';
import { normalizeRoleKey } from '../utils/organizationRoleUtils';
import {
    formatNotificationMessage,
    formatNotificationTitle,
    isPaymentReceivedNotification
} from '../utils/notificationFormatter';
import PaymentReceivedToast, { playPaymentChime } from '../components/common/PaymentReceivedToast';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

const POLLING_INTERVAL = 30 * 1000; // 30 secondes

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated, selectedOrganization } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [callbackMsg, setCallbackMsg] = useState(null);
    const [destination, setDestination] = useState('');
    const [pollingIntervalId, setPollingIntervalId] = useState(null);
    const [startDisplaying, setStartDisplaying] = useState(false);
    const [newNotifications, setNewNotifications] = useState([]);
    const [unViewed, setUnViewed] = useState(0);
    const clientRef = useRef(null);
    const [paymentAlert, setPaymentAlert] = useState(null);
    const knownIdsRef = useRef(new Set());
    const isCashierRef = useRef(false);

    useEffect(() => {
        knownIdsRef.current = new Set(notifications.map((n) => n.id));
    }, [notifications]);

    useEffect(() => {
        const storedRole = localStorage.getItem('netikash_selected_role');
        isCashierRef.current = normalizeRoleKey(
            selectedOrganization?.selectedRole || storedRole || selectedOrganization?.role
        ) !== 'admin';
    }, [selectedOrganization]);

    /** Caissier : alerte visible + sonore quand un paiement arrive sur sa caisse. */
    const announcePayment = useCallback((notification) => {
        if (!isCashierRef.current || !isPaymentReceivedNotification(notification)) return;
        setPaymentAlert({
            id: notification.id,
            title: formatNotificationTitle(notification),
            message: formatNotificationMessage(notification),
        });
        playPaymentChime();
    }, []);

    const closePaymentAlert = useCallback(() => setPaymentAlert(null), []);

    const safeParse = (value) => {
        try {
            return value ? JSON.parse(value) : null;
        } catch {
            return null;
        }
    };

    // ntd comme sur mobile : admin = organisation, caissier = user (selectedRole prioritaire)
    const getDestination = useCallback(() => {
        const listenerId = getTransactionListenerId(selectedOrganization, user);
        if (listenerId) return listenerId;
        if (destination) return destination;
        const org = safeParse(localStorage.getItem(storageKeys.organisation));
        return org?.id || '';
    }, [destination, selectedOrganization, user]);

    const fetchPeriodicNotifications = useCallback(async () => {
        const dst = getDestination();
        if (!dst) return;

        try {
            const data = await fetchNotifications(dst);
            setNewNotifications(data);
        } catch (error) {
            console.error('Error fetching periodic notifications:', error);
        }
    }, [getDestination]);

    const startPolling = useCallback(() => {
        if (pollingIntervalId) return;
        const intervalId = setInterval(fetchPeriodicNotifications, POLLING_INTERVAL);
        setPollingIntervalId(intervalId);
    }, [pollingIntervalId, fetchPeriodicNotifications]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalId) {
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
        }
    }, [pollingIntervalId]);

    const connectWebSocket = useCallback(async () => {
        if (!isAuthenticated || !user) return;

        try {
            const dst = getDestination();
            if (!dst) return;

            const listenerId = getTransactionListenerId(selectedOrganization, user);
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const socketUrl = `${apiUrl}/ws/v1/msg`;

            const socket = new SockJS(socketUrl);
            const client = new Client({
                webSocketFactory: () => socket,
                reconnectDelay: 5000,
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000,
                onConnect: () => {
                    console.log('WebSocket connected');
                    /* Notifications : même ntd que mobile (org id admin / user id caissier) */
                    if (dst) {
                        const notifPath = `/user/${dst}/pos-notification-listener`;
                        client.subscribe(notifPath, (message) => {
                            try {
                                const content = JSON.parse(message.body);
                                const notification = JSON.parse(content.body);

                                if (notification.type === 'callback') {
                                    if (notification.status !== 'accepted') {
                                        setCallbackMsg(notification);
                                    }
                                } else {
                                    if (!['delete', 'update'].includes(notification.type)
                                        && !knownIdsRef.current.has(notification.id)) {
                                        announcePayment(notification);
                                    }
                                    setNotifications((prev) => {
                                        const exists = prev.some((n) => n.id === notification.id);

                                        if (notification.type === 'delete') {
                                            return prev.filter((n) => n.id !== notification.id);
                                        } else if (notification.type === 'update') {
                                            return prev.map((n) =>
                                                n.id === notification.id ? { ...n, msgRead: true } : n
                                            );
                                        } else if (!exists) {
                                            return [notification, ...prev];
                                        }

                                        return prev;
                                    });
                                }
                            } catch (error) {
                                console.error('Error parsing WebSocket message:', error);
                            }
                        });
                    }
                    /* Suivi paiement en direct (comme mobile WaitingPaymentScreen) */
                    if (listenerId) {
                        const txPath = `/user/${listenerId}/transaction-listener`;
                        client.subscribe(txPath, (message) => {
                            const body = parseTransactionListenerMessage(message.body);
                            if (!body) return;
                            const ref = extractListenerRef(body);
                            const status = body.status;
                            const detail = { ref, status, body };

                            if (ref && isPaymentApprovedStatus(status)) {
                                window.dispatchEvent(new CustomEvent('paymentRequest:approved', { detail }));
                                window.dispatchEvent(new CustomEvent('transaction:created', {
                                    detail: { organizationId: selectedOrganization?.id }
                                }));
                            }
                            window.dispatchEvent(new CustomEvent('transaction:listener', { detail }));
                        });
                        console.log('[WebSocket] Abonné transaction-listener:', txPath);
                    }
                },
                onStompError: (frame) => {
                    console.error('STOMP error:', frame);
                },
                onDisconnect: () => {
                    console.log('WebSocket disconnected');
                }
            });

            client.activate();
            clientRef.current = client;
        } catch (error) {
            console.error('Error connecting WebSocket:', error);
        }
    }, [isAuthenticated, user, selectedOrganization, getDestination, announcePayment]);

    const disconnectWebSocket = useCallback(() => {
        if (clientRef.current) {
            clientRef.current.deactivate();
            clientRef.current = null;
        }
    }, []);

    const handleMarkAsRead = useCallback(async (notificationId) => {
        const dst = getDestination();
        if (!dst) return;

        try {
            await markNotificationAsRead(notificationId, dst);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notificationId ? { ...n, msgRead: true } : n))
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, [getDestination]);

    const handleDelete = useCallback(async (notificationId) => {
        const dst = getDestination();
        if (!dst) return;

        try {
            await deleteNotification(notificationId, dst);
            setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    }, [getDestination]);

    useEffect(() => {
        if (isAuthenticated) {
            const org = safeParse(localStorage.getItem(storageKeys.organisation));
            if (org?.id) {
                setDestination(org.id);
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && user) {
            connectWebSocket();
            startPolling();
        } else {
            disconnectWebSocket();
            stopPolling();
        }

        return () => {
            disconnectWebSocket();
            stopPolling();
        };
    }, [
        isAuthenticated,
        user,
        selectedOrganization?.id,
        selectedOrganization?.selectedRole,
        selectedOrganization?.role,
        connectWebSocket,
        disconnectWebSocket,
        startPolling,
        stopPolling
    ]);

    useEffect(() => {
        if (startDisplaying && newNotifications.length > 0) {
            const uniqueNotifications = newNotifications.filter(
                (newNotif) => !notifications.some((notif) => notif.id === newNotif.id)
            );

            if (uniqueNotifications.length > 0) {
                setNotifications(newNotifications);
            }
        }
    }, [newNotifications, startDisplaying, notifications]);

    const initialFetch = useCallback(async () => {
        const dst = getDestination();
        if (!dst) return;

        try {
            const data = await fetchNotifications(dst);
            setNotifications(data);
            setStartDisplaying(true);
        } catch (error) {
            console.error('Error fetching initial notifications:', error);
        }
    }, [getDestination]);

    useEffect(() => {
        if (isAuthenticated && destination) {
            initialFetch();
        }
    }, [isAuthenticated, destination, initialFetch]);

    // Récupérer le compteur de notifications non lues (comme sur mobile)
    const fetchUnViewedCount = useCallback(async () => {
        if (!isAuthenticated || !user || !selectedOrganization) return;

        try {
            // Déterminer ntd selon le rôle (comme sur mobile)
            const ntd = getTransactionListenerId(selectedOrganization, user);

            if (!ntd) return;

            const count = await fetchNotificationCountByStatus({
                ntd,
                sts: 'received',
                chl: 'socket',
                zon: 'dropdown'
            });

            // L'API retourne un nombre directement ou un objet
            const unViewedCount = typeof count === 'number' ? count : (count?.count || count || 0);
            setUnViewed(unViewedCount > 0 ? unViewedCount : 0);
        } catch (error) {
            console.error('Error fetching unviewed notification count:', error);
            setUnViewed(0);
        }
    }, [isAuthenticated, user, selectedOrganization]);

    // Mettre à jour le compteur périodiquement
    useEffect(() => {
        if (isAuthenticated && user && selectedOrganization) {
            fetchUnViewedCount();
            const intervalId = setInterval(fetchUnViewedCount, 30000); // Toutes les 30 secondes
            return () => clearInterval(intervalId);
        }
    }, [isAuthenticated, user, selectedOrganization, fetchUnViewedCount]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                setNotifications,
                callbackMsg,
                setCallbackMsg,
                startPolling,
                stopPolling,
                setStartDisplaying,
                markAsRead: handleMarkAsRead,
                deleteNotification: handleDelete,
                destination: getDestination(),
                unViewed,
                setUnViewed,
                fetchUnViewedCount
            }}
        >
            {children}
            <PaymentReceivedToast alert={paymentAlert} onClose={closePaymentAlert} />
        </NotificationContext.Provider>
    );
};

